/**
 * AgroTech — Temporal Analysis  (Step 2 of the 9-step intelligence loop)
 *
 * Select a field, choose a date range, fetch Sentinel-2 imagery via GEE,
 * and view NDVI / NDRE / NDWI / LST indices over time.
 *
 * Fetch-state persistence
 * ───────────────────────
 * GEE fetches are asynchronous background jobs that can take 1–3 minutes.
 * When the user navigates away and back, we must not lose the "waiting" context.
 *
 * Solution: pending fetches are written to localStorage under the key
 *   nabhos_gee_pending_<fieldId>_<startDate>_<endDate>
 * The entry contains a timestamp (so stale entries older than 30 min are discarded).
 * On mount, any matching pending entry for the current field + date range triggers
 * an automatic polling loop (every 15 s) until imagery is returned.
 * The Fetch button is disabled while a pending entry exists for the same window,
 * preventing duplicate submissions.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Satellite, CloudOff, RefreshCw, BarChart2, Calendar, Loader2, Info } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { satelliteBoundariesApi, satelliteImageryApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Field {
  id: string;
  name: string;
  area_hectares?: number;
}

interface VegetationIndex {
  index_type: string;
  mean_value: number;
  min_value: number;
  max_value: number;
  std_dev: number;
}

interface ImageryRecord {
  id: string;
  acquisition_date: string;
  satellite: string;
  cloud_cover_percentage: number;
  metadata?: { imageId?: string };
  indices: VegetationIndex[];
}

// ─── Pending-fetch localStorage helpers ──────────────────────────────────────

const PENDING_TTL_MS = 30 * 60 * 1000; // 30 minutes — discard stale entries

function pendingKey(fieldId: string, start: string, end: string) {
  return `nabhos_gee_pending_${fieldId}_${start}_${end}`;
}

function setPending(fieldId: string, start: string, end: string) {
  try {
    localStorage.setItem(
      pendingKey(fieldId, start, end),
      JSON.stringify({ queuedAt: Date.now() })
    );
  } catch { /* localStorage unavailable */ }
}

function clearPending(fieldId: string, start: string, end: string) {
  try { localStorage.removeItem(pendingKey(fieldId, start, end)); } catch { /* noop */ }
}

function isPending(fieldId: string, start: string, end: string): boolean {
  try {
    const raw = localStorage.getItem(pendingKey(fieldId, start, end));
    if (!raw) return false;
    const { queuedAt } = JSON.parse(raw) as { queuedAt: number };
    if (Date.now() - queuedAt > PENDING_TTL_MS) {
      localStorage.removeItem(pendingKey(fieldId, start, end));
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function pendingAge(fieldId: string, start: string, end: string): number | null {
  try {
    const raw = localStorage.getItem(pendingKey(fieldId, start, end));
    if (!raw) return null;
    const { queuedAt } = JSON.parse(raw) as { queuedAt: number };
    return Math.round((Date.now() - queuedAt) / 1000);
  } catch { return null; }
}

// ─── Index metadata ───────────────────────────────────────────────────────────

const INDEX_META: Record<string, { label: string; desc: string; goodColour: string; badColour: string }> = {
  NDVI: { label: 'NDVI', desc: 'Crop vigour & chlorophyll content',       goodColour: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',     badColour: 'bg-red-100 text-red-700' },
  NDRE: { label: 'NDRE', desc: 'Early water / nitrogen stress',           goodColour: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',         badColour: 'bg-orange-100 text-orange-700' },
  NDWI: { label: 'NDWI', desc: 'Soil moisture & waterlogging',            goodColour: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',         badColour: 'bg-red-100 text-red-700' },
  LST:  { label: 'LST',  desc: 'Surface temperature / heat stress',       goodColour: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', badColour: 'bg-red-200 text-red-800' },
  EVI:  { label: 'EVI',  desc: 'Vigour in high-biomass canopy areas',     goodColour: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', badColour: 'bg-amber-100 text-amber-700' },
};

function IndexBadge({ type, value }: { type: string; value: number | null }) {
  const meta = INDEX_META[type];
  if (!meta || value == null) return <span className="text-xs text-gray-400">—</span>;
  const n = Number(value); // node-postgres returns DECIMAL columns as strings
  const isGood = type === 'LST' ? n < 40 : n > 0.3;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium ${isGood ? meta.goodColour : meta.badColour}`}>
      {n.toFixed(3)}
    </span>
  );
}

function CloudBadge({ pct }: { pct: number | null }) {
  if (pct == null) return null;
  const n = Number(pct); // node-postgres returns DECIMAL columns as strings
  const cls = n < 10 ? 'bg-green-100 text-green-700' : n < 30 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      <CloudOff className="w-3 h-3" />{n.toFixed(1)}%
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000; // 30 seconds — halves polling rate vs. original 15 s

export const TemporalAnalysisPage: React.FC = () => {
  const [fields, setFields]                   = useState<Field[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [imagery, setImagery]                 = useState<ImageryRecord[]>([]);
  const [loadingFields, setLoadingFields]     = useState(true);
  const [fetching, setFetching]               = useState(false);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState<string | null>(null);

  // Data availability hint — earliest and latest stored record dates for the selected field
  const [dataRange, setDataRange] = useState<{ earliest: string; latest: string } | null>(null);

  // Pending state — true when a GEE job has been queued for the current window
  const [pendingFetch, setPendingFetch]       = useState(false);
  const [pendingSeconds, setPendingSeconds]   = useState<number | null>(null);
  const pollRef                               = useRef<ReturnType<typeof setInterval> | null>(null);

  // Date range — default to last 90 days
  const today     = new Date().toISOString().slice(0, 10);
  const ninetyAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(ninetyAgo);
  const [endDate,   setEndDate]   = useState(today);
  const [maxCloud,  setMaxCloud]  = useState(20);

  const [activeIndex, setActiveIndex] = useState<string>('NDVI');

  // ── Stop polling helper ────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // ── Load fields ────────────────────────────────────────────────────────────
  useEffect(() => {
    satelliteBoundariesApi.getAll()
      .then((res) => setFields(res.data?.boundaries ?? []))
      .catch(() => {})
      .finally(() => setLoadingFields(false));
  }, []);

  // ── Data availability hint — fetch all stored records for the field (no date filter)
  // to discover earliest / latest acquisition dates without an extra API endpoint.
  useEffect(() => {
    setDataRange(null);
    if (!selectedFieldId) return;
    satelliteImageryApi.getImagery(selectedFieldId)
      .then((res) => {
        const records: ImageryRecord[] = res.imagery ?? [];
        if (records.length === 0) return;
        const dates = records.map((r) => r.acquisition_date).sort();
        setDataRange({ earliest: dates[0].slice(0, 10), latest: dates[dates.length - 1].slice(0, 10) });
      })
      .catch(() => {}); // non-critical — silently ignore
  }, [selectedFieldId]);

  // ── Load imagery ───────────────────────────────────────────────────────────
  const loadImagery = useCallback(async (fieldId: string, start: string, end: string): Promise<ImageryRecord[]> => {
    setLoading(true);
    try {
      // satelliteImageryApi.getImagery already returns response.data (the axios .data is
      // unwrapped inside the api helper), so the shape here is { count, imagery: [...] }
      // — NOT a nested { data: { imagery } }.
      const res = await satelliteImageryApi.getImagery(fieldId, { startDate: start, endDate: end });
      const records: ImageryRecord[] = res.imagery ?? [];
      setImagery(records);
      setError(null);
      // Keep data-range hint up to date whenever new records arrive
      if (records.length > 0) {
        const dates = records.map((r) => r.acquisition_date).sort();
        setDataRange((prev) => {
          const earliest = prev ? (dates[0].slice(0, 10) < prev.earliest ? dates[0].slice(0, 10) : prev.earliest) : dates[0].slice(0, 10);
          const latest   = prev ? (dates[dates.length - 1].slice(0, 10) > prev.latest ? dates[dates.length - 1].slice(0, 10) : prev.latest) : dates[dates.length - 1].slice(0, 10);
          return { earliest, latest };
        });
      }
      return records;
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load imagery');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Start polling until imagery arrives or TTL expires ────────────────────
  const startPolling = useCallback(async (fieldId: string, start: string, end: string) => {
    stopPolling();
    setPendingFetch(true);
    setPendingSeconds(pendingAge(fieldId, start, end) ?? 0);

    // Small random jitter (0–5 s) so multiple open tabs don't fire in lockstep
    const jitter = Math.random() * 5_000;
    await new Promise(r => setTimeout(r, jitter));

    pollRef.current = setInterval(async () => {
      // Update elapsed seconds display
      const age = pendingAge(fieldId, start, end);
      if (age === null) {
        // Entry expired (>30 min) or was cleared
        stopPolling();
        setPendingFetch(false);
        return;
      }
      setPendingSeconds(age);

      const records = await loadImagery(fieldId, start, end);
      if (records.length > 0) {
        // Data has arrived — stop polling and clear the pending marker
        stopPolling();
        setPendingFetch(false);
        clearPending(fieldId, start, end);
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling, loadImagery]);

  // ── On field or date change: load imagery + check for pending fetch ────────
  useEffect(() => {
    stopPolling();
    setPendingFetch(false);
    setImagery([]);

    if (!selectedFieldId) return;

    // Load whatever is already stored
    loadImagery(selectedFieldId, startDate, endDate).then((records) => {
      // Check localStorage for a queued-but-not-yet-complete fetch
      if (isPending(selectedFieldId, startDate, endDate)) {
        if (records.length === 0) {
          // Still waiting — start polling
          startPolling(selectedFieldId, startDate, endDate);
        } else {
          // Imagery arrived while user was away — clear the marker
          clearPending(selectedFieldId, startDate, endDate);
        }
      }
    });

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFieldId, startDate, endDate]);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── Fetch new imagery from GEE ────────────────────────────────────────────
  // The backend fetchImagery controller is synchronous: it awaits the entire GEE
  // computation before returning 200, so imagery is already in the DB by the time
  // the POST resolves.  We therefore load immediately on success.  We only fall back
  // to the polling path if the immediate load still returns nothing (e.g. the backend
  // switches to a true async queue in a future version).
  const handleFetch = async () => {
    if (!selectedFieldId) { setError('Select a field first'); return; }
    setFetching(true); setError(null);
    try {
      await satelliteImageryApi.fetchImagery(selectedFieldId, { startDate, endDate, maxCloudCover: maxCloud });
      // Immediately load the now-stored records
      const records = await loadImagery(selectedFieldId, startDate, endDate);
      if (records.length === 0) {
        // Nothing came back yet — fall back to polling (async queue / delayed storage)
        setPending(selectedFieldId, startDate, endDate);
        startPolling(selectedFieldId, startDate, endDate);
      }
      // If records > 0, imagery is already set in state by loadImagery — no polling needed
    } catch (e: any) {
      const d = e?.response?.data;
      setError(
        d?.error?.message ?? (typeof d?.error === 'string' ? d.error : null) ?? d?.message ??
        e?.message ?? 'GEE fetch failed — check that this field has a valid polygon geometry'
      );
    } finally {
      setFetching(false);
    }
  };

  // Cancel a pending fetch (clears the localStorage marker and stops polling)
  const handleCancelPending = () => {
    clearPending(selectedFieldId, startDate, endDate);
    stopPolling();
    setPendingFetch(false);
    setPendingSeconds(null);
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const selectedField  = fields.find((f) => f.id === selectedFieldId);
  const activeIndices  = Object.keys(INDEX_META);

  const indexMeans: Record<string, number | null> = {};
  activeIndices.forEach((idx) => {
    // node-postgres returns DECIMAL columns as strings — coerce with Number()
    const vals = imagery.flatMap((im) =>
      im.indices.filter((i) => i.index_type === idx).map((i) => Number(i.mean_value))
    );
    indexMeans[idx] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  });

  // The Fetch button should be disabled when a pending entry exists for this exact window
  const fetchDisabled = fetching || pendingFetch;

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  // Human-readable elapsed time
  const elapsedLabel =
    pendingSeconds === null ? null :
    pendingSeconds < 60    ? `${pendingSeconds}s ago` :
    `${Math.floor(pendingSeconds / 60)}m ${pendingSeconds % 60}s ago`;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Satellite className="w-7 h-7 text-blue-600" /> Temporal Analysis
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Fetch Sentinel-2 satellite imagery and view NDVI / NDRE / NDWI / LST indices over time — Step 2
        </p>
      </div>

      {/* ── Error ── */}
      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Pending fetch banner — persists across navigation ── */}
      {pendingFetch && (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
              Google Earth Engine is processing your imagery request
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
              Queued {elapsedLabel ? `${elapsedLabel}` : 'just now'} · Checking every 15 s · This typically takes 1–3 minutes
            </p>
          </div>
          <button
            onClick={handleCancelPending}
            className="text-xs text-blue-600 dark:text-blue-400 underline flex-shrink-0 hover:text-blue-800"
            title="Stop waiting and dismiss this notice"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Controls ── */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Field</label>
            {loadingFields ? <Spinner size="sm" /> : (
              <select
                value={selectedFieldId}
                onChange={(e) => setSelectedFieldId(e.target.value)}
                className={`w-full ${SELECT_CLS}`}
              >
                <option value="">Select a field…</option>
                {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={SELECT_CLS}
            />
            {dataRange && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 whitespace-nowrap">
                <Info className="w-3 h-3 flex-shrink-0" />
                Earliest stored: <button
                  type="button"
                  className="underline hover:text-primary-600 ml-0.5"
                  onClick={() => setStartDate(dataRange.earliest)}
                >{dataRange.earliest}</button>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={SELECT_CLS}
            />
            {dataRange && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 whitespace-nowrap">
                <Info className="w-3 h-3 flex-shrink-0" />
                Latest stored: <button
                  type="button"
                  className="underline hover:text-primary-600 ml-0.5"
                  onClick={() => setEndDate(dataRange.latest)}
                >{dataRange.latest}</button>
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Max Cloud Cover
              <span className="ml-1 font-semibold text-primary-600">{maxCloud}%</span>
            </label>
            <input
              type="range"
              min={5}
              max={80}
              step={5}
              value={maxCloud}
              onChange={(e) => setMaxCloud(Number(e.target.value))}
              className="w-full"
            />
            <Button
              onClick={handleFetch}
              loading={fetching}
              disabled={fetchDisabled}
              fullWidth
              title={pendingFetch ? 'A fetch is already in progress for this field and date range' : 'Fetch Sentinel-2 imagery from Google Earth Engine'}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              {fetching ? 'Queuing…' : 'Fetch'}
            </Button>
            {pendingFetch && (
              <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                <Info className="w-3 h-3 flex-shrink-0" />
                Fetch in progress — button disabled to prevent duplicates
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* ── No field selected ── */}
      {!selectedFieldId && (
        <Card>
          <div className="text-center py-12">
            <Satellite className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">Select a field to view satellite imagery</p>
            <p className="text-sm text-gray-400 mt-1">
              Choose a field above, then click <strong>Fetch</strong> to retrieve Sentinel-2 data from Google Earth Engine.
              Results take 1–3 minutes and are automatically polled.
            </p>
          </div>
        </Card>
      )}

      {selectedFieldId && (
        <>
          {/* ── Index summary cards ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {activeIndices.map((idx) => {
              const meta = INDEX_META[idx];
              const val  = indexMeans[idx];
              return (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`text-left p-4 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    activeIndex === idx
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-primary-300'
                  }`}
                >
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">{meta.label}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {val != null ? Number(val).toFixed(3) : '—'}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-tight">{meta.desc}</p>
                </button>
              );
            })}
          </div>

          {/* ── Imagery timeline ── */}
          <Card>
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                Imagery Timeline — {selectedField?.name}
                <span className="text-sm font-normal text-gray-400">
                  ({imagery.length} acquisition{imagery.length !== 1 ? 's' : ''})
                </span>
                {pendingFetch && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    <Loader2 className="w-3 h-3 animate-spin" /> fetching…
                  </span>
                )}
              </h2>
              <div className="flex gap-1.5 flex-wrap">
                {activeIndices.map((idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      activeIndex === idx
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {idx}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-10"><Spinner size="lg" /></div>
            ) : imagery.length === 0 ? (
              <div className="text-center py-10">
                <BarChart2 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                {pendingFetch ? (
                  <>
                    <p className="text-gray-600 dark:text-gray-300 font-medium flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      Waiting for Google Earth Engine…
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Imagery will appear here automatically. Checking every 15 seconds.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">No imagery for this field and date range</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Click <strong>Fetch</strong> to request Sentinel-2 imagery from Google Earth Engine
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      {['Date', 'Satellite', 'Cloud', ...activeIndices.map((i) => INDEX_META[i].label)].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {[...imagery]
                      .sort((a, b) => b.acquisition_date.localeCompare(a.acquisition_date))
                      .map((im) => (
                        <tr key={im.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                            {new Date(im.acquisition_date).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })}
                          </td>
                          <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{im.satellite}</td>
                          <td className="px-3 py-2.5"><CloudBadge pct={im.cloud_cover_percentage} /></td>
                          {activeIndices.map((idx) => {
                            const iv = im.indices.find((i) => i.index_type === idx);
                            return (
                              <td key={idx} className="px-3 py-2.5">
                                <IndexBadge type={idx} value={iv?.mean_value ?? null} />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default TemporalAnalysisPage;

// Made with Bob
